import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { BiArrowToLeft, BiEdit, BiPlus, BiUser, BiCreditCard, BiBuilding, BiCheck, BiX, BiDownload, BiMinus, BiChevronDown, BiChevronUp, BiCog, BiShare, BiListUl } from 'react-icons/bi'
import { Shield, CreditCard, CheckCircle, X } from 'lucide-react'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import { agencyService, Agency } from '../services/agencyService'
import paymentService, { Invoice, Subscription } from '../services/paymentService'
import { Agent } from '../services/agentService'
import { formatInvoiceAmount } from '../utils/currency'
import { useCurrentUser } from '../hooks/useCurrentUser'
import { usePagination } from '../hooks/usePagination'

import Alert from '../components/Alert'
import Pagination from '../components/Pagination'

import InviteAgentModal from '../components/InviteAgentModal'
import AddAgentModal from '../components/AddAgentModal'
import EditAgentModal from '../components/EditAgentModal'
import UpdateSubscriptionModal from '../components/UpdateSubscriptionModal'
import ShareCodeModal from '../components/ShareCodeModal'
import AgentDetailsModal from '../components/AgentDetailsModal'

interface AgencyStats {
  totalAgents: number
  activeAgents: number
  pendingAgents: number
  licensedAgents: number
  unlicensedAgents: number
  agencyPaidLicenses: number
  selfPaidLicenses: number
  recentLogins: number
}

interface EditableAgencyData {
  name: string
  agencyCode: string
  phone: string
  website: string
  address: string
  description: string
}

interface EditableBillingData {
  companyName: string
  billingEmail: string
  address: string
  city: string
  postalCode: string
  country: string
  vatNumber: string
}

interface ValidationErrors {
  name?: string
  agencyCode?: string
  phone?: string
  website?: string
  address?: string
  description?: string
}

interface BillingValidationErrors {
  companyName?: string
  billingEmail?: string
  address?: string
  city?: string
  postalCode?: string
  country?: string
  vatNumber?: string
}


// Helper functions for currency formatting
const penceToDecimals = (pence: number): string => {
  return (pence / 100).toFixed(2)
}

const decimalsToPence = (decimals: string): number => {
  const parsed = parseFloat(decimals)
  return isNaN(parsed) ? 0 : Math.round(parsed * 100)
}

const formatCurrencyInput = (value: string): string => {
  // Remove any non-numeric characters except decimal point
  const cleaned = value.replace(/[^\d.]/g, '')

  // Split by decimal point
  const parts = cleaned.split('.')

  // If more than one decimal point, keep only the first
  if (parts.length > 2) {
    return parts[0] + '.' + parts.slice(1).join('')
  }

  // Limit to 2 decimal places
  if (parts[1] && parts[1].length > 2) {
    parts[1] = parts[1].substring(0, 2)
  }

  return parts.join('.')
}

const formatCurrencyForSave = (value: string): string => {
  // Convert to decimal format, adding .00 if it's a whole number
  const cleaned = value.replace(/[^\d.]/g, '')
  const parsed = parseFloat(cleaned)

  if (isNaN(parsed)) return '1.00'

  // If it's a whole number, add .00
  if (Number.isInteger(parsed)) {
    return parsed.toFixed(2)
  }

  // If it has decimals, ensure 2 decimal places
  return parsed.toFixed(2)
}

// Validation schema for billing settings
const billingSettingsSchema = Yup.object().shape({
  defaultAgencySeatCost: Yup.string()
    .required('Agency seat cost is required')
    .test('is-valid-currency', 'Must be £1.00 or more', value => {
      if (!value) return false

      // Use formatCurrencyForSave to get the proper decimal format
      const formattedValue = formatCurrencyForSave(value)
      const numValue = parseFloat(formattedValue)

      if (isNaN(numValue)) return false
      return numValue >= 1.00
    }),
  defaultAgencyBasePlanCost: Yup.string()
    .required('Agency base plan cost is required')
    .test('is-valid-currency', 'Must be £1.00 or more', value => {
      if (!value) return false

      // Use formatCurrencyForSave to get the proper decimal format
      const formattedValue = formatCurrencyForSave(value)
      const numValue = parseFloat(formattedValue)

      if (isNaN(numValue)) return false
      return numValue >= 1.00
    })
})

const AgencyProfile = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useCurrentUser()
  const [searchParams, setSearchParams] = useSearchParams()
  const [agency, setAgency] = useState<Agency | null>(null)
  const [agents, setAgents] = useState<Agent[]>([])
  const [joinRequests, setJoinRequests] = useState<Agent[]>([])
  const [stats, setStats] = useState<AgencyStats | null>(null)
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [subscription, setSubscription] = useState<Subscription | null>(null)

  // Get initial tab from URL params, default to 'overview'
  const getInitialTab = () => {
    const tabFromUrl = searchParams.get('tab')
    const validTabs = ['overview', 'agents', 'billing', 'settings', 'audit']
    return validTabs.includes(tabFromUrl || '') ? tabFromUrl : 'overview'
  }

  const [activeTab, setActiveTabState] = useState(getInitialTab())
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false)
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Pagination for agents list
  const {
    pagination,
    searchTerm,
    handlePageChange,
    handleLimitChange,
    handleSearch,
    handleResetSearch,
    updateTotal
  } = usePagination(10)

  // Filter and sorting states
  const [roleFilter, setRoleFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [paymentFilter, setPaymentFilter] = useState<string>('')
  const [licenseFilter, setLicenseFilter] = useState<string>('')
  const [sortField, setSortField] = useState<string>('')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [editData, setEditData] = useState<EditableAgencyData>({
    name: '',
    agencyCode: '',
    phone: '',
    website: '',
    address: '',
    description: ''
  })

  // Billing editing states
  const [isEditingBilling, setIsEditingBilling] = useState(false)
  const [isSavingBilling, setIsSavingBilling] = useState(false)
  const [editBillingData, setEditBillingData] = useState<EditableBillingData>({
    companyName: '',
    billingEmail: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'GB',
    vatNumber: ''
  })

  // Validation states
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})
  const [billingValidationErrors, setBillingValidationErrors] = useState<BillingValidationErrors>({})
  const [isCheckingCode, setIsCheckingCode] = useState(false)
  const [codeAvailable, setCodeAvailable] = useState<boolean | null>(null)
  const [originalAgencyCode, setOriginalAgencyCode] = useState('')
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('')
  const [billingMessage, setBillingMessage] = useState('')
  const [billingMessageType, setBillingMessageType] = useState<'success' | 'error' | ''>('')
  const [settingsMessage, setSettingsMessage] = useState('')
  const [settingsMessageType, setSettingsMessageType] = useState<'success' | 'error' | ''>('')

  // Invite modal states
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showAddAgentModal, setShowAddAgentModal] = useState(false)

  // Share code modal states
  const [showShareCodeModal, setShowShareCodeModal] = useState(false)

  // Edit agent modal states
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null)

  // Agent details modal states
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedAgentForDetails, setSelectedAgentForDetails] = useState<Agent | null>(null)

  // Cancel invite modal states
  const [showCancelInviteModal, setShowCancelInviteModal] = useState(false)
  const [agentToCancel, setAgentToCancel] = useState<Agent | null>(null)
  const [isCancellingInvite, setIsCancellingInvite] = useState(false)

  // Delete agent modal states
  const [showDeleteAgentModal, setShowDeleteAgentModal] = useState(false)
  const [agentToDelete, setAgentToDelete] = useState<Agent | null>(null)
  const [isDeletingAgent, setIsDeletingAgent] = useState(false)

  // Sync agents state
  const [isSyncingAgents, setIsSyncingAgents] = useState(false)

  // Seat management states

  // Settings editing states
  const [isEditingSettings, setIsEditingSettings] = useState(false)
  const [isSavingSettings, setIsSavingSettings] = useState(false)
  const [editSettingsData, setEditSettingsData] = useState({
    defaultAgencySeatCost: 1499,
    defaultAgencyBasePlanCost: 4999,
    isActive: true
  })

  // Seat display states (read-only)
  const [currentSeats, setCurrentSeats] = useState(0)

  // Subscription accordion states
  const [expandedSubscriptions, setExpandedSubscriptions] = useState<Set<string>>(new Set())

  // Add state for join request modal
  const [showJoinRequestModal, setShowJoinRequestModal] = useState(false)
  const [joinRequestToAccept, setJoinRequestToAccept] = useState<Agent | null>(null)
  const [joinRequestRole, setJoinRequestRole] = useState<'agent' | 'admin'>('agent')
  const [joinRequestPayment, setJoinRequestPayment] = useState<'agent' | 'agency'>('agency')
  const [isAcceptingJoinRequest, setIsAcceptingJoinRequest] = useState(false)

  // Helper function to get agency base plan cost in pence
  const getAgencyBasePlanCost = (agency: Agency | null): number => {
    if (!agency) return 4999 // fallback

    // First try to get from actual Stripe subscription pricing
    if (subscription && subscription.items && subscription.items.length > 0) {
      // Look for the base plan item (usually the first item, or one with agency_plan metadata)
      const basePlanItem = subscription.items.find(item => {
        // If we have agency-specific pricing, this should be the base plan
        return item.price.unit_amount && item.price.unit_amount > 0
      })

      if (basePlanItem) {
        console.log('🔍 Found base plan item in subscription:', basePlanItem)
        return basePlanItem.price.unit_amount // Already in pence
      }
    }

    // Second try to get from actual subscription product pricing (legacy)
    const subscriptions = getAgencySubscriptions(agency)
    if (subscriptions.length > 0) {
      const latestSubscription = subscriptions[0]
      const basePlanProduct = latestSubscription.stripeProducts?.find(product => product.productType === 'agency_plan')
      if (basePlanProduct && basePlanProduct.unitPrice !== undefined) {
        console.log('🔍 Found base plan in stripeProducts:', basePlanProduct)
        return basePlanProduct.unitPrice // Already in pence, could be 0
      }
    }

    // Fallback to agency settings - handle 0 explicitly
    const fallbackPrice = agency.settings?.defaultAgencyBasePlanCost !== undefined ? agency.settings.defaultAgencyBasePlanCost : 4999
    console.log('🔍 Using fallback price from agency settings:', fallbackPrice)
    return fallbackPrice
  }

  // Helper function to get agency seat cost in pence
  const getAgencySeatCost = (agency: Agency | null): number => {
    if (!agency) return 1499 // fallback

    // First try to get from actual subscription product pricing
    const subscriptions = getAgencySubscriptions(agency)
    if (subscriptions.length > 0) {
      const latestSubscription = subscriptions[0]
      const seatProduct = latestSubscription.stripeProducts?.find(product => product.productType === 'agent_seat')
      if (seatProduct && seatProduct.unitPrice !== undefined) {
        return seatProduct.unitPrice // Already in pence, could be 0
      }
    }

    // Fallback to agency settings - handle 0 explicitly
    return agency.settings?.defaultAgencySeatCost !== undefined ? agency.settings.defaultAgencySeatCost : 1499
  }

  // Helper function to check if user is system admin
  const isSystemAdmin = (): boolean => {
    // System admins can access everything
    return user?.roles?.includes('admin') || false
  }

  // Helper function to get subscriptions from billing.subscriptions
  const getAgencySubscriptions = (agency: Agency | null) => {
    if (!agency) return []
    return agency.billing?.subscriptions || []
  }

  // Helper function to check if agency has an active subscription
  const hasActiveSubscription = (agency: Agency | null) => {
    if (!agency) return false
    const subscriptions = getAgencySubscriptions(agency)
    return subscriptions.length > 0 && subscriptions.some(sub => sub.stripeSubscriptionStatus === true)
  }

  // Calculate stats directly from agent data
  const calculateAgentStats = (agency: Agency | null) => {
    if (!agency || !agency.agents) {
      return {
        totalAgents: 0,
        activeAgents: 0,
        pendingAgents: 0,
        licensedAgents: 0,
        unlicensedAgents: 0,
        agencyPaidLicenses: 0,
        selfPaidLicenses: 0,
        recentLogins: 0
      }
    }

    const agents = agency.agents
    const totalAgents = agents.length
    const activeAgents = agents.filter(agent => agent.invite.status === 'accepted').length
    const pendingAgents = agents.filter(agent => agent.invite.status === 'pending').length
    const licensedAgents = agents.filter(agent => agent.license.status === 'licensed').length
    const unlicensedAgents = agents.filter(agent => agent.license.status === 'none' || agent.license.status === 'trial').length
    const agencyPaidLicenses = agents.filter(agent =>
      agent.license.status === 'licensed' && agent.paymentResponsibility === 'agency'
    ).length
    const selfPaidLicenses = agents.filter(agent =>
      agent.license.status === 'licensed' && agent.paymentResponsibility === 'agent'
    ).length

    // For recent logins, we'd need login data which isn't available in this response
    const recentLogins = 0

    return {
      totalAgents,
      activeAgents,
      pendingAgents,
      licensedAgents,
      unlicensedAgents,
      agencyPaidLicenses,
      selfPaidLicenses,
      recentLogins
    }
  }

  // Function to update active tab and URL params
  const setActiveTab = (tabId: string) => {
    setActiveTabState(tabId)
    const newSearchParams = new URLSearchParams(searchParams)
    if (tabId === 'overview') {
      // Remove tab param for overview (default)
      newSearchParams.delete('tab')
    } else {
      newSearchParams.set('tab', tabId)
    }
    setSearchParams(newSearchParams, { replace: true })
  }

  // Sync tab state when URL changes (browser back/forward)
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab')
    const validTabs = ['overview', 'agents', 'billing', 'settings', 'audit']
    const newTab = validTabs.includes(tabFromUrl || '') ? tabFromUrl : 'overview'
    if (newTab !== activeTab) {
      setActiveTabState(newTab || 'overview')
    }
  }, [searchParams, activeTab])

  // Debounce utility function
  const debounce = (func: Function, wait: number) => {
    let timeout: number
    return function executedFunction(...args: any[]) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }

  // Debounced function to check agency code availability
  const checkAgencyCodeAvailability = useCallback(
    debounce(async (agencyCode: string) => {
      if (!agencyCode || agencyCode.length < 2) {
        setCodeAvailable(null)
        return
      }

      // Don't check if it's the same as the original code
      if (agencyCode === originalAgencyCode) {
        setCodeAvailable(true)
        return
      }

      if (!/^[a-z0-9-]+$/.test(agencyCode)) {
        setCodeAvailable(false)
        return
      }

      setIsCheckingCode(true)

      try {
        const response = await agencyService.checkAgencyCodeAvailability(agencyCode, agency?.id)
        setCodeAvailable(response.available)
      } catch (error) {
        console.error('Error checking agency code availability:', error)
        setCodeAvailable(false)
      } finally {
        setIsCheckingCode(false)
      }
    }, 1000),
    [originalAgencyCode]
  )

  // Validation functions
  const validateField = (field: keyof EditableAgencyData, value: string): string | null => {
    switch (field) {
      case 'name':
        if (!value.trim()) return 'Agency name is required'
        if (value.trim().length < 2) return 'Agency name must be at least 2 characters long'
        return null

      case 'agencyCode':
        if (!value.trim()) return 'Agency code is required'
        if (value.trim().length < 2) return 'Agency code must be at least 2 characters long'
        if (!/^[a-z0-9-]+$/.test(value)) return 'Agency code can only contain lowercase letters, numbers, and hyphens'
        if (codeAvailable === false && value !== originalAgencyCode) return 'This agency code is already taken'
        return null

      case 'website':
        if (value && value.trim() && !isValidUrl(value)) return 'Please enter a valid URL (e.g., https://example.com)'
        return null

      default:
        return null
    }
  }

  const validateBillingField = (field: keyof EditableBillingData, value: string): string | null => {
    switch (field) {
      case 'companyName':
        if (!value.trim()) return 'Company name is required'
        if (value.trim().length < 2) return 'Company name must be at least 2 characters long'
        return null

      case 'billingEmail':
        if (!value.trim()) return 'Billing email is required'
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address'
        return null

      case 'address':
        if (!value.trim()) return 'Address is required'
        return null

      case 'city':
        if (!value.trim()) return 'City is required'
        return null

      case 'postalCode':
        if (!value.trim()) return 'Postal code is required'
        return null

      case 'country':
        if (!value.trim()) return 'Country is required'
        return null

      default:
        return null
    }
  }

  const isValidUrl = (string: string): boolean => {
    try {
      new URL(string)
      return true
    } catch (_) {
      return false
    }
  }

  const validateAllFields = (): boolean => {
    const errors: ValidationErrors = {}
    let isValid = true

    // Validate each field
    Object.keys(editData).forEach((key) => {
      const field = key as keyof EditableAgencyData
      const error = validateField(field, editData[field])
      if (error) {
        errors[field] = error
        isValid = false
      }
    })

    // Check agency code availability
    if (editData.agencyCode !== originalAgencyCode && codeAvailable === false) {
      errors.agencyCode = 'This agency code is already taken'
      isValid = false
    }

    setValidationErrors(errors)
    return isValid
  }

  const validateAllBillingFields = (): boolean => {
    const errors: BillingValidationErrors = {}
    let isValid = true

    // Validate each field
    Object.keys(editBillingData).forEach((key) => {
      const field = key as keyof EditableBillingData
      const error = validateBillingField(field, editBillingData[field])
      if (error) {
        errors[field] = error
        isValid = false
      }
    })

    setBillingValidationErrors(errors)
    return isValid
  }

  // Removed fetchAgencyMembers - now handled directly in this component

  useEffect(() => {
    if (id) {
      fetchAgencyData()
    }
  }, [id])

  // Fetch agents separately with pagination
  useEffect(() => {
    if (id && agency && activeTab === 'agents') {
      fetchAgents()
    }
  }, [id, agency, activeTab, pagination.page, pagination.limit, searchTerm, roleFilter, statusFilter, paymentFilter, licenseFilter, sortField, sortDirection])

  const fetchAgents = async () => {
    if (!id) return

    try {
      setIsLoading(true)
      const { agents: agentsData, pagination: paginationData } = await agencyService.getAgents(id, {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        role: roleFilter || undefined,
        status: statusFilter || undefined,
        paymentResponsibility: paymentFilter || undefined,
        license: licenseFilter || undefined,
        sortBy: sortField || undefined,
        sortDirection: sortDirection || undefined
      })

      setAgents(agentsData)
      updateTotal(paginationData.total)
    } catch (err) {
      console.error('Error fetching agents:', err)
      setError('Failed to load agents. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchAgencyData = async () => {
    try {
      setIsLoading(true)
      setError(null)

      const [agencyData, agencyStats, joinRequestsData] = await Promise.all([
        agencyService.getAgencyById(id!),
        agencyService.getAgencyStats(id!),
        agencyService.getJoinRequests(id!)
      ])

      setAgency(agencyData)
      setJoinRequests(joinRequestsData)
      setStats(agencyStats.stats)

      // Fetch agents separately if we're on the agents tab
      if (activeTab === 'agents') {
        const { agents: agentsData, pagination: paginationData } = await agencyService.getAgents(id!, {
          page: pagination.page,
          limit: pagination.limit,
          search: searchTerm
        })
        setAgents(agentsData)
        updateTotal(paginationData.total)
      }

      // Initialize edit data
      const initialEditData = {
        name: agencyData.name,
        agencyCode: agencyData.agencyCode,
        phone: agencyData.phone || '',
        website: agencyData.website || '',
        address: agencyData.address || '',
        description: agencyData.description || ''
      }
      setEditData(initialEditData)
      setOriginalAgencyCode(agencyData.agencyCode)

      // Initialize billing edit data
      const billingInfo = agencyData.billing?.billingInfo || agencyData.billingInfo || {}
      const initialBillingData = {
        companyName: billingInfo.companyName || '',
        billingEmail: billingInfo.billingEmail || '',
        address: billingInfo.address || '',
        city: billingInfo.city || '',
        postalCode: billingInfo.postalCode || '',
        country: billingInfo.country || 'GB',
        vatNumber: billingInfo.vatNumber || ''
      }
      setEditBillingData(initialBillingData)

      // Initialize settings edit data
      const initialSettingsData = {
        defaultAgencySeatCost: agencyData.settings?.defaultAgencySeatCost !== undefined ? agencyData.settings.defaultAgencySeatCost : 1499,
        defaultAgencyBasePlanCost: agencyData.settings?.defaultAgencyBasePlanCost !== undefined ? agencyData.settings.defaultAgencyBasePlanCost : 4999,
        isActive: agencyData.isActive !== false
      }
      setEditSettingsData(initialSettingsData)

      // Initialize seat count for display (only count licensed non-admin agents with agency payment responsibility)
      const billableAgents = agencyData.agents?.filter(agent =>
        agent.role !== 'admin' &&
        agent.license.status === 'licensed' &&
        agent.paymentResponsibility === 'agency'
      ) || []
      const seatCount = billableAgents.length
      setCurrentSeats(seatCount)

      // Fetch subscription and invoices if subscriptions exist
      const subscriptions = getAgencySubscriptions(agencyData)
      if (subscriptions.length > 0) {
        // Get the latest subscription for detailed information
        const latestSubscription = subscriptions
          .sort((a, b) => new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime())[0]

        if (latestSubscription?.stripeSubscriptionId) {
          await Promise.all([
            fetchInvoices(agencyData.id),
            fetchSubscription(latestSubscription.stripeSubscriptionId, agencyData)
          ])
        }
      }
    } catch (err) {
      console.error('Error fetching agency data:', err)
      setError('Failed to load agency data. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchInvoices = async (agencyId: string) => {
    try {
      setIsLoadingInvoices(true)
      const response = await paymentService.getInvoicesByAgencyId(agencyId)

      if (response.success && response.data) {
        setInvoices(response.data)
      } else {
        console.error('Failed to fetch invoices:', response.error)
      }
    } catch (err) {
      console.error('Error fetching invoices:', err)
    } finally {
      setIsLoadingInvoices(false)
    }
  }

  const fetchSubscription = async (subscriptionId: string, agencyData?: Agency) => {
    try {
      setIsLoadingSubscription(true)
      const response = await paymentService.getSubscription(subscriptionId)

      if (response.success && response.data) {
        setSubscription(response.data.subscription)

        // Calculate seats from subscription items for display
        // Use dynamic pricing if available, fallback to default
        const targetAgency = agencyData || agency
        let seatCostInPence = 1499 // default fallback

        if (targetAgency) {
          seatCostInPence = getAgencySeatCost(targetAgency)
        }

        const additionalSeats = response.data.subscription.items.reduce((total, item) => {
          // Check if this is a seat item using dynamic pricing
          if (item.price.unit_amount === seatCostInPence) {
            return total + item.quantity
          }
          return total
        }, 0)

        // Always calculate seats based on licensed non-admin agents with agency payment responsibility
        // This ensures only billable agents are counted as seats
        if (targetAgency) {
          const billableAgents = targetAgency.agents?.filter(agent =>
            agent.role !== 'admin' &&
            agent.license.status === 'licensed' &&
            agent.paymentResponsibility === 'agency'
          ) || []
          const calculatedSeats = billableAgents.length
          setCurrentSeats(calculatedSeats)
        } else {
          setCurrentSeats(additionalSeats)
        }
      } else {
        console.error('Failed to fetch subscription:', response.error)
      }
    } catch (err) {
      console.error('Error fetching subscription:', err)
    } finally {
      setIsLoadingSubscription(false)
    }
  }

  const handleEdit = () => {
    setIsEditing(true)
    setMessage('')
    setMessageType('')
    setValidationErrors({})
    setCodeAvailable(null)
  }

  const handleCancel = () => {
    if (!agency) return

    // Reset edit data to original values
    setEditData({
      name: agency.name,
      agencyCode: agency.agencyCode,
      phone: agency.phone || '',
      website: agency.website || '',
      address: agency.address || '',
      description: agency.description || ''
    })
    setIsEditing(false)
    setMessage('')
    setMessageType('')
    setValidationErrors({})
    setCodeAvailable(null)
  }

  const handleEditBilling = () => {
    setIsEditingBilling(true)
    setBillingMessage('')
    setBillingMessageType('')
    setBillingValidationErrors({})
  }

  const handleCancelBilling = () => {
    if (!agency) return

    // Reset billing edit data to original values
    const billingInfo = agency.billing?.billingInfo || agency.billingInfo || {}
    setEditBillingData({
      companyName: billingInfo.companyName || '',
      billingEmail: billingInfo.billingEmail || '',
      address: billingInfo.address || '',
      city: billingInfo.city || '',
      postalCode: billingInfo.postalCode || '',
      country: billingInfo.country || 'GB',
      vatNumber: billingInfo.vatNumber || ''
    })
    setIsEditingBilling(false)
    setBillingMessage('')
    setBillingMessageType('')
    setBillingValidationErrors({})
  }

  const handleSaveSettings = async () => {
    if (!agency) return

    // Clear previous messages
    setSettingsMessage('')
    setSettingsMessageType('')

    try {
      setIsSavingSettings(true)

      const updatedAgency = await agencyService.updateAgency(agency.id, {
        settings: {
          defaultAgencySeatCost: editSettingsData.defaultAgencySeatCost,
          defaultAgencyBasePlanCost: editSettingsData.defaultAgencyBasePlanCost
        }
      })

      setAgency(updatedAgency)
      setIsEditingSettings(false)
      setSettingsMessage('Settings updated successfully!')
      setSettingsMessageType('success')

      setTimeout(() => {
        setSettingsMessage('')
        setSettingsMessageType('')
      }, 3000)
    } catch (err: any) {
      console.error('Error updating agency settings:', err)
      setSettingsMessage(err?.message || 'Failed to update agency settings. Please try again.')
      setSettingsMessageType('error')
    } finally {
      setIsSavingSettings(false)
    }
  }

  const handleEditSettings = () => {
    setIsEditingSettings(true)
    setSettingsMessage('')
    setSettingsMessageType('')
  }

  const handleCancelSettings = () => {
    if (!agency) return

    // Reset settings edit data to original values
    setEditSettingsData({
      defaultAgencySeatCost: agency.settings?.defaultAgencySeatCost !== undefined ? agency.settings.defaultAgencySeatCost : 1499,
      defaultAgencyBasePlanCost: agency.settings?.defaultAgencyBasePlanCost !== undefined ? agency.settings.defaultAgencyBasePlanCost : 4999,
      isActive: agency.isActive !== false
    })
    setIsEditingSettings(false)
    setSettingsMessage('')
    setSettingsMessageType('')
  }

  const handleSave = async () => {
    if (!agency) return

    // Clear previous messages
    setMessage('')
    setMessageType('')

    // Validate all fields
    if (!validateAllFields()) {
      setMessage('Please fix the validation errors before saving.')
      setMessageType('error')
      return
    }

    try {
      setIsSaving(true)

      const updatedAgency = await agencyService.updateAgency(agency.id, {
        name: editData.name,
        agencyCode: editData.agencyCode,
        phone: editData.phone,
        website: editData.website,
        address: editData.address,
        description: editData.description
      })

      setAgency(updatedAgency)
      setOriginalAgencyCode(updatedAgency.agencyCode)
      setIsEditing(false)
      setValidationErrors({})
      setCodeAvailable(null)
      setMessage('Agency updated successfully!')
      setMessageType('success')

      setTimeout(() => {
        setMessage('')
        setMessageType('')
      }, 3000)
    } catch (err: any) {
      console.error('Error updating agency:', err)
      setMessage(err?.message || 'Failed to update agency. Please try again.')
      setMessageType('error')
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveBilling = async () => {
    if (!agency) return

    // Clear previous messages
    setBillingMessage('')
    setBillingMessageType('')

    // Validate all fields
    if (!validateAllBillingFields()) {
      setBillingMessage('Please fix the validation errors before saving.')
      setBillingMessageType('error')
      return
    }

    try {
      setIsSavingBilling(true)

      const updatedAgency = await agencyService.updateAgency(agency.id, {
        billing: {
          billingInfo: {
            companyName: editBillingData.companyName,
            billingEmail: editBillingData.billingEmail,
            address: editBillingData.address,
            city: editBillingData.city,
            postalCode: editBillingData.postalCode,
            country: editBillingData.country,
            vatNumber: editBillingData.vatNumber
          }
        }
      })

      setAgency(updatedAgency)
      setIsEditingBilling(false)
      setBillingValidationErrors({})
      setBillingMessage('Billing information updated successfully!')
      setBillingMessageType('success')

      setTimeout(() => {
        setBillingMessage('')
        setBillingMessageType('')
      }, 3000)
    } catch (err: any) {
      console.error('Error updating billing information:', err)
      setBillingMessage(err?.message || 'Failed to update billing information. Please try again.')
      setBillingMessageType('error')
    } finally {
      setIsSavingBilling(false)
    }
  }

  const handleInputChange = (field: keyof EditableAgencyData, value: string) => {
    let processedValue = value

    // Special processing for agency code
    if (field === 'agencyCode') {
      processedValue = value.toLowerCase().replace(/[^a-z0-9-]/g, '')
      // Check availability when code changes
      checkAgencyCodeAvailability(processedValue)
    }

    setEditData(prev => ({
      ...prev,
      [field]: processedValue
    }))

    // Real-time validation
    const error = validateField(field, processedValue)
    setValidationErrors(prev => ({
      ...prev,
      [field]: error || undefined
    }))

    // Clear save error when user starts typing
    if (message) {
      setMessage('')
      setMessageType('')
    }
  }

  const handleBillingInputChange = (field: keyof EditableBillingData, value: string) => {
    setEditBillingData(prev => ({
      ...prev,
      [field]: value
    }))

    // Real-time validation
    const error = validateBillingField(field, value)
    setBillingValidationErrors(prev => ({
      ...prev,
      [field]: error || undefined
    }))

    // Clear save error when user starts typing
    if (billingMessage) {
      setBillingMessage('')
      setBillingMessageType('')
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const formatLastLogin = (dateString?: string) => {
    if (!dateString) return 'Never'

    const now = new Date()
    const loginDate = new Date(dateString)
    const diffInDays = Math.floor((now.getTime() - loginDate.getTime()) / (1000 * 60 * 60 * 24))

    if (diffInDays === 0) return 'Today'
    if (diffInDays === 1) return '1 day ago'
    if (diffInDays < 30) return `${diffInDays} days ago`

    return formatDate(dateString)
  }

  // Agent modal handlers
  const handleEditAgent = (agent: Agent) => {
    // Prevent editing the agency owner
    if (agent.userId._id === agency?.ownedBy) {
      return
    }
    setSelectedAgent(agent)
    setShowEditModal(true)
  }

  const handleViewAgentDetails = (agent: Agent) => {
    setSelectedAgentForDetails(agent)
    setShowDetailsModal(true)
  }

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false)
    setSelectedAgentForDetails(null)
  }

  const handleCloseEditModal = () => {
    setShowEditModal(false)
    setSelectedAgent(null)
  }

  const handleAgentUpdated = () => {
    // Refresh agents data after update
    if (activeTab === 'agents') {
      fetchAgents()
    } else {
      fetchAgencyData()
    }
  }

  const handleCancelInvite = (agent: Agent) => {
    setAgentToCancel(agent)
    setShowCancelInviteModal(true)
  }

  const handleConfirmCancelInvite = async () => {
    if (!agentToCancel) return

    try {
      setIsCancellingInvite(true)
      await agencyService.revokeAgentAccess(agency?.id || '', agentToCancel.userId._id)

      // Refresh the data
      if (activeTab === 'agents') {
        await fetchAgents()
      } else {
        await fetchAgencyData()
      }

      // Close modal and show success message
      setShowCancelInviteModal(false)
      setAgentToCancel(null)
      alert('Invitation cancelled successfully')
    } catch (err: any) {
      console.error('Error cancelling invitation:', err)
      alert(err?.message || 'Failed to cancel invitation. Please try again.')
    } finally {
      setIsCancellingInvite(false)
    }
  }

  const handleCloseCancelInviteModal = () => {
    setShowCancelInviteModal(false)
    setAgentToCancel(null)
  }

  const handleDeleteAgent = (agent: Agent) => {
    // Prevent deleting the agency owner
    if (agent.userId._id === agency?.ownedBy) {
      return
    }
    setAgentToDelete(agent)
    setShowDeleteAgentModal(true)
  }

  const handleConfirmDeleteAgent = async () => {
    if (!agentToDelete) return

    try {
      setIsDeletingAgent(true)
      await agencyService.deleteAgent(agency?.id || '', agentToDelete.userId._id)

      // Refresh the data
      if (activeTab === 'agents') {
        await fetchAgents()
      } else {
        await fetchAgencyData()
      }

      // Close modal and show success message
      setShowDeleteAgentModal(false)
      setAgentToDelete(null)
      setMessage('Agent deleted successfully')
      setMessageType('success')

      setTimeout(() => {
        setMessage('')
        setMessageType('')
      }, 3000)
    } catch (err: any) {
      console.error('Error deleting agent:', err)
      setMessage(err?.message || 'Failed to delete agent. Please try again.')
      setMessageType('error')
    } finally {
      setIsDeletingAgent(false)
    }
  }

  const handleCloseDeleteAgentModal = () => {
    setShowDeleteAgentModal(false)
    setAgentToDelete(null)
  }

  const handleSyncAgents = async () => {
    if (!agency) return

    try {
      setIsSyncingAgents(true)
      const result = await agencyService.syncAgencyAgents(agency.id)

      setMessage(`Successfully synced ${result.syncedCount} agents with quote-away`)
      setMessageType('success')

      setTimeout(() => {
        setMessage('')
        setMessageType('')
      }, 3000)
    } catch (err: any) {
      console.error('Error syncing agents:', err)
      setMessage(err?.message || 'Failed to sync agents. Please try again.')
      setMessageType('error')
    } finally {
      setIsSyncingAgents(false)
    }
  }

  // Filter handlers
  const handleRoleFilterChange = (value: string) => {
    setRoleFilter(value)
    handlePageChange(1) // Reset to first page when filter changes
  }

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value)
    handlePageChange(1)
  }

  const handlePaymentFilterChange = (value: string) => {
    setPaymentFilter(value)
    handlePageChange(1)
  }

  const handleLicenseFilterChange = (value: string) => {
    setLicenseFilter(value)
    handlePageChange(1)
  }

  const handleResetFilters = () => {
    setRoleFilter('')
    setStatusFilter('')
    setPaymentFilter('')
    setLicenseFilter('')
    handleResetSearch()
    handlePageChange(1)
  }

  // Sorting handler
  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // New field, default to ascending
      setSortField(field)
      setSortDirection('asc')
    }
    handlePageChange(1) // Reset to first page when sorting changes
  }

  const handleAcceptJoinRequest = async (request: Agent) => {
    if (!agency) return

    try {
      await agencyService.updateAgentStatus(agency.id, request.userId._id, 'accepted')

      // Refresh the data
      await fetchAgencyData() // Need to refresh join requests too
      if (activeTab === 'agents') {
        await fetchAgents()
      }

      setMessage('Join request accepted successfully!')
      setMessageType('success')
    } catch (err: any) {
      console.error('Error accepting join request:', err)
      setError(err?.message || 'Failed to accept join request')
    }
  }

  const handleRejectJoinRequest = async (request: Agent) => {
    if (!agency) return

    try {
      await agencyService.updateAgentStatus(agency.id, request.userId._id, 'declined')

      // Refresh the data
      await fetchAgencyData() // Need to refresh join requests too

      setMessage('Join request rejected successfully!')
      setMessageType('success')
    } catch (err: any) {
      console.error('Error rejecting join request:', err)
      setError(err?.message || 'Failed to reject join request')
    }
  }

  // Subscription accordion handlers
  const toggleSubscriptionExpansion = (subscriptionId: string) => {
    setExpandedSubscriptions(prev => {
      const newSet = new Set(prev)
      if (newSet.has(subscriptionId)) {
        newSet.delete(subscriptionId)
      } else {
        newSet.add(subscriptionId)
      }
      return newSet
    })
  }

  // Invite system handlers

  // Handler to open modal
  const openJoinRequestModal = (request: Agent) => {
    setJoinRequestToAccept(request)
    setJoinRequestRole(request.role)
    setJoinRequestPayment(request.paymentResponsibility === 'none' ? 'agency' : request.paymentResponsibility)
    setShowJoinRequestModal(true)
  }

  // Handler to close modal
  const closeJoinRequestModal = () => {
    setShowJoinRequestModal(false)
    setJoinRequestToAccept(null)
  }

  // Handler to confirm accept
  const handleConfirmAcceptJoinRequest = async () => {
    if (!agency || !joinRequestToAccept) return
    setIsAcceptingJoinRequest(true)
    try {
      // Try to get the user ID from different possible properties
      let userId: string | undefined

      if (typeof joinRequestToAccept.userId === 'string') {
        userId = joinRequestToAccept.userId
      } else if (joinRequestToAccept.userId?._id) {
        userId = joinRequestToAccept.userId._id
      } else if (joinRequestToAccept.user?._id) {
        userId = joinRequestToAccept.user._id
      }

      if (!userId) {
        console.error('Could not find user ID in join request:', joinRequestToAccept)
        throw new Error('Invalid user data')
      }

      // Update agent status, role, and payment responsibility
      await agencyService.updateAgentStatus(agency.id, userId, 'accepted', joinRequestRole, joinRequestPayment)
      await fetchAgencyData() // Need to refresh join requests too
      if (activeTab === 'agents') {
        await fetchAgents()
      }
      setMessage('Join request accepted successfully!')
      setMessageType('success')
      closeJoinRequestModal()
    } catch (err: any) {
      console.error('Error accepting join request:', err)
      setError(err?.message || 'Failed to accept join request')
    } finally {
      setIsAcceptingJoinRequest(false)
    }
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BiBuilding },
    { id: 'agents', label: 'Agents', icon: BiUser },
    { id: 'billing', label: 'Billing', icon: BiCreditCard },
    ...(isSystemAdmin() ? [
      { id: 'settings', label: 'Settings', icon: BiCog },
      { id: 'audit', label: 'Audit', icon: BiListUl }
    ] : []),
  ]

  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-slate-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={fetchAgencyData}
                  className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200 mr-3"
                >
                  Try Again
                </button>
                <button
                  onClick={() => navigate('/agencies')}
                  className="inline-flex items-center space-x-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 px-3 py-1.5 rounded-lg font-medium transition-colors"
                >
                  <BiArrowToLeft size={16} />
                  <span>Back to Agencies</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!agency) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
            Agency Not Found
          </h1>
          <button
            onClick={() => navigate('/agencies')}
            className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 dark:hover:bg-primary-900/20 px-4 py-2 rounded-lg font-medium transition-colors"
          >
            <BiArrowToLeft size={18} />
            <span>Back to Agencies</span>
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Alert */}
      {message && messageType && (
        <Alert
          type={messageType}
          message={message}
          onClose={() => {
            setMessage('')
            setMessageType('')
          }}
        />
      )}

      {/* No Active Subscription Alert */}
      {agency && !hasActiveSubscription(agency) && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <div className="text-sm text-red-700 dark:text-red-300">
                <p>
                  You need an active subscription to access all features.
                  <button
                    onClick={() => navigate(`/agencies/create?step=2&agency=${agency.id}`)}
                    className="ml-2 underline hover:no-underline font-medium"
                  >
                    Subscribe now
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/agencies')}
          className="inline-flex items-center space-x-2 text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-slate-50 dark:hover:bg-slate-800 px-3 py-2 rounded-lg transition-all duration-200 mb-6 group"
        >
          <BiArrowToLeft size={20} className="group-hover:-translate-x-0.5 transition-transform duration-200" />
          <span className="font-medium">Back to Agencies</span>
        </button>

        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
              {agency.name}
            </h1>
            <div className="mt-2 space-y-1">
              <p className="text-slate-600 dark:text-slate-400">
                Agency Code: <span className="font-mono font-medium text-slate-800 dark:text-slate-200">{agency.agencyCode}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-slate-200 dark:border-slate-700 mb-8">
        <nav className="flex space-x-1 bg-slate-50 dark:bg-slate-800/50 rounded-lg p-1 mb-6">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2.5 rounded-md font-medium text-sm transition-all duration-200 min-w-0 flex-1 justify-center ${activeTab === tab.id
                  ? 'bg-white dark:bg-slate-700 text-primary-600 dark:text-primary-400 shadow-sm border border-slate-200 dark:border-slate-600'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-white/50 dark:hover:bg-slate-700/50'
                  }`}
              >
                <Icon size={18} className={activeTab === tab.id ? 'text-primary-600 dark:text-primary-400' : ''} />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {/* Basic Information */}
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6 md:col-span-2">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Basic Information
                </h3>
                <div className="flex items-center space-x-2">
                  {isEditing ? (
                    <>
                      <button
                        onClick={handleCancel}
                        disabled={isSaving}
                        className="inline-flex items-center space-x-2 px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <BiX size={16} />
                        <span>Cancel</span>
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={
                          isSaving ||
                          isCheckingCode ||
                          (codeAvailable === false && editData.agencyCode !== originalAgencyCode) ||
                          Object.keys(validationErrors).some(key => validationErrors[key as keyof ValidationErrors])
                        }
                        className="inline-flex items-center space-x-2 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-slate-400 disabled:cursor-not-allowed rounded-lg transition-colors disabled:opacity-50"
                      >
                        {isSaving ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Saving...</span>
                          </>
                        ) : (
                          <>
                            <BiCheck size={16} />
                            <span>Save</span>
                          </>
                        )}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleEdit}
                      className="inline-flex items-center space-x-2 px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    >
                      <BiEdit size={16} />
                      <span>Edit</span>
                    </button>
                  )}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Agency Name
                  </label>
                  <input
                    type="text"
                    value={isEditing ? editData.name : agency.name}
                    onChange={isEditing ? (e) => handleInputChange('name', e.target.value) : undefined}
                    disabled={!isEditing}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isEditing
                      ? validationErrors.name
                        ? 'border-red-300 dark:border-red-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                        : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                      : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 cursor-not-allowed'
                      }`}
                    placeholder={isEditing ? "Enter agency name" : ""}
                  />
                  {isEditing && validationErrors.name && (
                    <p className="text-red-500 text-sm mt-1">{validationErrors.name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Agency Code
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={agency.agencyCode}
                      disabled={true}
                      className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 cursor-not-allowed rounded-lg font-mono"
                      readOnly
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <svg className="w-4 h-4 text-slate-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={isEditing ? editData.phone : (agency.phone || '')}
                    onChange={isEditing ? (e) => handleInputChange('phone', e.target.value) : undefined}
                    disabled={!isEditing}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isEditing
                      ? validationErrors.phone
                        ? 'border-red-300 dark:border-red-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                        : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                      : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 cursor-not-allowed'
                      }`}
                    placeholder={isEditing ? "Enter phone number" : (!agency.phone ? "Not provided" : "")}
                  />
                  {isEditing && validationErrors.phone && (
                    <p className="text-red-500 text-sm mt-1">{validationErrors.phone}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Website
                  </label>
                  <input
                    type="url"
                    value={isEditing ? editData.website : (agency.website || '')}
                    onChange={isEditing ? (e) => handleInputChange('website', e.target.value) : undefined}
                    disabled={!isEditing}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isEditing
                      ? validationErrors.website
                        ? 'border-red-300 dark:border-red-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                        : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                      : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 cursor-not-allowed'
                      }`}
                    placeholder={isEditing ? "https://example.com" : (!agency.website ? "Not provided" : "")}
                  />
                  {isEditing && validationErrors.website && (
                    <p className="text-red-500 text-sm mt-1">{validationErrors.website}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Address
                  </label>
                  <input
                    type="text"
                    value={isEditing ? editData.address : (agency.address || '')}
                    onChange={isEditing ? (e) => handleInputChange('address', e.target.value) : undefined}
                    disabled={!isEditing}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isEditing
                      ? validationErrors.address
                        ? 'border-red-300 dark:border-red-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                        : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                      : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 cursor-not-allowed'
                      }`}
                    placeholder={isEditing ? "Enter address" : (!agency.address ? "Not provided" : "")}
                  />
                  {isEditing && validationErrors.address && (
                    <p className="text-red-500 text-sm mt-1">{validationErrors.address}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={isEditing ? editData.description : (agency.description || '')}
                    onChange={isEditing ? (e) => handleInputChange('description', e.target.value) : undefined}
                    rows={3}
                    disabled={!isEditing}
                    className={`w-full px-3 py-2 border rounded-lg resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isEditing
                      ? validationErrors.description
                        ? 'border-red-300 dark:border-red-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                        : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                      : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 cursor-not-allowed'
                      }`}
                    placeholder={isEditing ? "Enter agency description" : (!agency.description ? "Not provided" : "")}
                  />
                  {isEditing && validationErrors.description && (
                    <p className="text-red-500 text-sm mt-1">{validationErrors.description}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="space-y-6">
              <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                  Agent Statistics
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600 dark:text-slate-400">Total Agents</span>
                    <span className="text-2xl font-bold text-slate-900 dark:text-white">{calculateAgentStats(agency).totalAgents}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600 dark:text-slate-400">Active Agents</span>
                    <span className="text-lg font-semibold text-green-600">{calculateAgentStats(agency).activeAgents}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600 dark:text-slate-400">Pending Agents</span>
                    <span className="text-lg font-semibold text-yellow-600">{calculateAgentStats(agency).pendingAgents}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                  License Statistics
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600 dark:text-slate-400">Licensed Agents</span>
                    <span className="text-lg font-semibold text-blue-600">{calculateAgentStats(agency).licensedAgents}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600 dark:text-slate-400">Agency Paid</span>
                    <span className="text-sm font-medium text-slate-900 dark:text-white">{calculateAgentStats(agency).agencyPaidLicenses}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600 dark:text-slate-400">Self Paid</span>
                    <span className="text-sm font-medium text-slate-900 dark:text-white">{calculateAgentStats(agency).selfPaidLicenses}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600 dark:text-slate-400">Recent Logins (30d)</span>
                    <span className="text-sm font-medium text-slate-900 dark:text-white">{calculateAgentStats(agency).recentLogins}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'agents' && (
          <div className="space-y-6">
            {/* Header with invite button */}
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Agency Members ({agents.length})
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Manage your agency's memberships and their permissions
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowShareCodeModal(true)}
                  disabled={!hasActiveSubscription(agency)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-slate-100 dark:disabled:hover:bg-slate-700"
                >
                  <BiShare className="w-4 h-4" />
                  Share Code
                </button>
                <button
                  onClick={() => setShowInviteModal(true)}
                  disabled={!hasActiveSubscription(agency)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary-600"
                >
                  <BiPlus className="w-4 h-4" />
                  Invite Agent
                </button>
                {user?.roles?.includes('admin') && (
                  <>
                    <button
                      onClick={() => setShowAddAgentModal(true)}
                      disabled={!hasActiveSubscription(agency)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-green-600"
                    >
                      <BiPlus className="w-4 h-4" />
                      Add Agent
                    </button>
                    <button
                      onClick={handleSyncAgents}
                      disabled={isSyncingAgents || !hasActiveSubscription(agency)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isSyncingAgents ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Syncing Agents...</span>
                        </>
                      ) : (
                        <>
                          <BiCog className="w-4 h-4" />
                          <span>Sync Agents</span>
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-lg">
                {error}
              </div>
            )}

            {/* Join Requests Section */}
            {joinRequests && joinRequests.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                  Join Requests ({joinRequests.length})
                </h3>
                <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                      <thead className="bg-slate-50 dark:bg-slate-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                            User
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                            Role
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                            Requested
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                        {joinRequests.map((request) => (
                          <tr key={request.userId._id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-slate-900 dark:text-white">
                                  {request.user?.firstName && request.user?.lastName
                                    ? `${request.user.firstName} ${request.user.lastName}`
                                    : 'Unknown User'
                                  }
                                </div>
                                <div className="text-sm text-slate-500 dark:text-slate-400">
                                  {request.user?.email || 'No email'}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${request.role === 'admin'
                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400'
                                : 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                                }`}>
                                {request.role === 'admin' ? 'Admin' : 'Agent'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-slate-500 dark:text-slate-400">
                                {formatDate(request.invite.createdAt)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end space-x-2">
                                <button
                                  onClick={() => openJoinRequestModal(request)}
                                  className="text-green-600 hover:text-green-900 dark:hover:text-green-400 px-2 py-1 rounded hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                                >
                                  Accept
                                </button>
                                <button
                                  onClick={() => handleRejectJoinRequest(request)}
                                  className="text-red-600 hover:text-red-900 dark:hover:text-red-400 px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                >
                                  Reject
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Search and Filter Bar */}
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
              <div className="flex flex-col gap-4">
                {/* Search and count row */}
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                  <div className="flex-1 max-w-md">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-slate-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                        placeholder="Search agents by name, email, or auth ID..."
                        className="block w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      {searchTerm && (
                        <button
                          onClick={handleResetSearch}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        >
                          <BiX className="h-5 w-5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                    <span>
                      Showing {((pagination.page - 1) * pagination.limit) + 1}-{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} agents
                    </span>
                  </div>
                </div>

                {/* Filters row */}
                <div className="flex flex-wrap gap-3 items-center">
                  {/* Role filter */}
                  <select
                    value={roleFilter}
                    onChange={(e) => handleRoleFilterChange(e.target.value)}
                    className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">All Roles</option>
                    <option value="admin">Admin</option>
                    <option value="agent">Agent</option>
                  </select>

                  {/* Status filter */}
                  <select
                    value={statusFilter}
                    onChange={(e) => handleStatusFilterChange(e.target.value)}
                    className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">All Statuses</option>
                    <option value="accepted">Active</option>
                    <option value="pending">Pending</option>
                    <option value="declined">Declined</option>
                    <option value="revoked">Revoked</option>
                  </select>

                  {/* Payment filter */}
                  <select
                    value={paymentFilter}
                    onChange={(e) => handlePaymentFilterChange(e.target.value)}
                    className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">All Payment Types</option>
                    <option value="agency">Agency Pays</option>
                    <option value="agent">Agent Pays</option>
                    <option value="none">No Payment Required</option>
                  </select>

                  {/* License filter */}
                  <select
                    value={licenseFilter}
                    onChange={(e) => handleLicenseFilterChange(e.target.value)}
                    className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">All Licenses</option>
                    <option value="licensed">Licensed</option>
                    <option value="trial">Trial</option>
                    <option value="none">No License</option>
                  </select>

                  {/* Reset filters button */}
                  {(roleFilter || statusFilter || paymentFilter || licenseFilter || searchTerm) && (
                    <button
                      onClick={handleResetFilters}
                      className="px-3 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              </div>
            </div>

            {agents && agents.length > 0 && (
              <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                    <thead className="bg-slate-50 dark:bg-slate-700">
                      <tr>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
                          onClick={() => handleSort('agent')}
                        >
                          <div className="flex items-center gap-2">
                            <span>Agent</span>
                            {sortField === 'agent' && (
                              <span className="text-primary-600 dark:text-primary-400">
                                {sortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </div>
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
                          onClick={() => handleSort('role')}
                        >
                          <div className="flex items-center gap-2">
                            <span>Role</span>
                            {sortField === 'role' && (
                              <span className="text-primary-600 dark:text-primary-400">
                                {sortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </div>
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
                          onClick={() => handleSort('status')}
                        >
                          <div className="flex items-center gap-2">
                            <span>Status</span>
                            {sortField === 'status' && (
                              <span className="text-primary-600 dark:text-primary-400">
                                {sortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </div>
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
                          onClick={() => handleSort('payment')}
                        >
                          <div className="flex items-center gap-2">
                            <span>Payment</span>
                            {sortField === 'payment' && (
                              <span className="text-primary-600 dark:text-primary-400">
                                {sortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </div>
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
                          onClick={() => handleSort('license')}
                        >
                          <div className="flex items-center gap-2">
                            <span>License</span>
                            {sortField === 'license' && (
                              <span className="text-primary-600 dark:text-primary-400">
                                {sortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </div>
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                      {agents.map((agent) => {
                        // Determine license status and styling
                        const getLicenseStatus = () => {
                          if (agent.license.status === 'none') {
                            return {
                              text: 'No License',
                              className: 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300'
                            }
                          }

                          if (agent.license.status === 'trial') {
                            // Check if trial has expired
                            const trialStartDate = new Date(agent.license.createdAt)
                            const trialPeriod = agency.settings?.defaultTrialPeriod || 7 // Default to 7 days
                            const trialEndDate = new Date(trialStartDate)
                            trialEndDate.setDate(trialEndDate.getDate() + trialPeriod)
                            const now = new Date()

                            if (now > trialEndDate) {
                              return {
                                text: 'Trial Expired',
                                className: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                              }
                            }

                            return {
                              text: 'Trial',
                              className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                            }
                          }

                          if (agent.paymentResponsibility === 'agent') {
                            return {
                              text: 'Self-Licensed',
                              className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                            }
                          }

                          return {
                            text: 'Licensed',
                            className: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                          }
                        }

                        const licenseStatus = getLicenseStatus()

                        return (
                          <tr key={agent.userId._id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-slate-900 dark:text-white">
                                  {agent.user?.firstName && agent.user?.lastName
                                    ? `${agent.user.firstName} ${agent.user.lastName}`
                                    : 'Unknown User'
                                  }
                                </div>
                                <div className="text-sm text-slate-500 dark:text-slate-400">
                                  {agent.user?.email || 'No email'}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${agent.role === 'admin'
                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400'
                                : 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                                }`}>
                                {agent.role === 'admin' ? 'Admin' : 'Agent'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${agent.invite.status === 'accepted'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                : agent.invite.status === 'revoked'
                                  ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                                  : agent.invite.status === 'declined'
                                    ? 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                                }`}>
                                {agent.invite.status === 'accepted' ? 'Active' :
                                  agent.invite.status === 'revoked' ? 'Revoked' :
                                    agent.invite.status === 'declined' ? 'Declined' : 'Pending'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${agent.paymentResponsibility === 'agency'
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                                : agent.paymentResponsibility === 'agent'
                                  ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400'
                                  : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                                }`}>
                                {agent.paymentResponsibility === 'agency' ? 'Agency Pays' :
                                  agent.paymentResponsibility === 'agent' ? 'Agent Pays' :
                                    'No Payment Required'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${licenseStatus.className}`}>
                                {licenseStatus.text}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end space-x-2">
                                <button
                                  onClick={() => handleViewAgentDetails(agent)}
                                  className="text-blue-600 hover:text-blue-900 dark:hover:text-blue-400 px-2 py-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                >
                                  Details
                                </button>
                                {agent.userId._id !== agency?.ownedBy && (
                                  <>
                                    <button
                                      onClick={() => handleEditAgent(agent)}
                                      className="text-primary-600 hover:text-primary-900 dark:hover:text-primary-400 px-2 py-1 rounded hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => handleDeleteAgent(agent)}
                                      className="text-red-600 hover:text-red-900 dark:hover:text-red-400 px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                    >
                                      Delete
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Pagination */}
            {agents && agents.length > 0 && (
              <Pagination
                currentPage={pagination.page}
                limit={pagination.limit}
                total={pagination.total}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
                onRefresh={fetchAgents}
                isLoading={isLoading}
              />
            )}
          </div>
        )}

        {activeTab === 'billing' && (
          <div className="space-y-6">
            {/* Top row: Billing Information + Invoices */}
            <div className="grid gap-6 md:grid-cols-2">
              {/* Billing Information */}
              <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                    Billing Information
                  </h3>
                  <div className="flex items-center space-x-2">
                    {isEditingBilling ? (
                      <>
                        <button
                          onClick={handleCancelBilling}
                          disabled={isSavingBilling}
                          className="inline-flex items-center space-x-2 px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
                        >
                          <BiX size={16} />
                          <span>Cancel</span>
                        </button>
                        <button
                          onClick={handleSaveBilling}
                          disabled={
                            isSavingBilling ||
                            Object.keys(billingValidationErrors).some(key => billingValidationErrors[key as keyof BillingValidationErrors])
                          }
                          className="inline-flex items-center space-x-2 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-slate-400 disabled:cursor-not-allowed rounded-lg transition-colors disabled:opacity-50"
                        >
                          {isSavingBilling ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              <span>Saving...</span>
                            </>
                          ) : (
                            <>
                              <BiCheck size={16} />
                              <span>Save</span>
                            </>
                          )}
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={handleEditBilling}
                        className="inline-flex items-center space-x-2 px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                      >
                        <BiEdit size={16} />
                        <span>Edit</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Alert */}
                {billingMessage && billingMessageType && (
                  <Alert
                    type={billingMessageType}
                    message={billingMessage}
                    onClose={() => {
                      setBillingMessage('')
                      setBillingMessageType('')
                    }}
                  />
                )}

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Company Name
                    </label>
                    <input
                      type="text"
                      value={isEditingBilling ? editBillingData.companyName : ((agency.billing?.billingInfo || agency.billingInfo)?.companyName || '')}
                      onChange={isEditingBilling ? (e) => handleBillingInputChange('companyName', e.target.value) : undefined}
                      disabled={!isEditingBilling}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isEditingBilling
                        ? billingValidationErrors.companyName
                          ? 'border-red-300 dark:border-red-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                          : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                        : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 cursor-not-allowed'
                        }`}
                      placeholder={isEditingBilling ? "Enter company name" : (!(agency.billing?.billingInfo || agency.billingInfo)?.companyName ? "Not provided" : "")}
                    />
                    {isEditingBilling && billingValidationErrors.companyName && (
                      <p className="text-red-500 text-sm mt-1">{billingValidationErrors.companyName}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Billing Email
                    </label>
                    <input
                      type="email"
                      value={((agency.billing?.billingInfo || agency.billingInfo)?.billingEmail || '')}
                      disabled={true}
                      className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 cursor-not-allowed rounded-lg"
                      placeholder={!(agency.billing?.billingInfo || agency.billingInfo)?.billingEmail ? "Not provided" : ""}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Address
                    </label>
                    <input
                      type="text"
                      value={isEditingBilling ? editBillingData.address : ((agency.billing?.billingInfo || agency.billingInfo)?.address || '')}
                      onChange={isEditingBilling ? (e) => handleBillingInputChange('address', e.target.value) : undefined}
                      disabled={!isEditingBilling}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isEditingBilling
                        ? billingValidationErrors.address
                          ? 'border-red-300 dark:border-red-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                          : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                        : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 cursor-not-allowed'
                        }`}
                      placeholder={isEditingBilling ? "Enter address" : (!(agency.billing?.billingInfo || agency.billingInfo)?.address ? "Not provided" : "")}
                    />
                    {isEditingBilling && billingValidationErrors.address && (
                      <p className="text-red-500 text-sm mt-1">{billingValidationErrors.address}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      value={isEditingBilling ? editBillingData.city : ((agency.billing?.billingInfo || agency.billingInfo)?.city || '')}
                      onChange={isEditingBilling ? (e) => handleBillingInputChange('city', e.target.value) : undefined}
                      disabled={!isEditingBilling}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isEditingBilling
                        ? billingValidationErrors.city
                          ? 'border-red-300 dark:border-red-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                          : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                        : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 cursor-not-allowed'
                        }`}
                      placeholder={isEditingBilling ? "Enter city" : (!(agency.billing?.billingInfo || agency.billingInfo)?.city ? "Not provided" : "")}
                    />
                    {isEditingBilling && billingValidationErrors.city && (
                      <p className="text-red-500 text-sm mt-1">{billingValidationErrors.city}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Postal Code
                    </label>
                    <input
                      type="text"
                      value={isEditingBilling ? editBillingData.postalCode : ((agency.billing?.billingInfo || agency.billingInfo)?.postalCode || '')}
                      onChange={isEditingBilling ? (e) => handleBillingInputChange('postalCode', e.target.value) : undefined}
                      disabled={!isEditingBilling}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isEditingBilling
                        ? billingValidationErrors.postalCode
                          ? 'border-red-300 dark:border-red-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                          : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                        : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 cursor-not-allowed'
                        }`}
                      placeholder={isEditingBilling ? "Enter postal code" : (!(agency.billing?.billingInfo || agency.billingInfo)?.postalCode ? "Not provided" : "")}
                    />
                    {isEditingBilling && billingValidationErrors.postalCode && (
                      <p className="text-red-500 text-sm mt-1">{billingValidationErrors.postalCode}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Country
                    </label>
                    {isEditingBilling ? (
                      <select
                        value={editBillingData.country}
                        onChange={(e) => handleBillingInputChange('country', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${billingValidationErrors.country
                          ? 'border-red-300 dark:border-red-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                          : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                          }`}
                      >
                        <option value="GB">United Kingdom</option>
                        <option value="US">United States</option>
                        <option value="CA">Canada</option>
                        <option value="AU">Australia</option>
                        <option value="DE">Germany</option>
                        <option value="FR">France</option>
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={((agency.billing?.billingInfo || agency.billingInfo)?.country === 'GB') ? 'United Kingdom' :
                          ((agency.billing?.billingInfo || agency.billingInfo)?.country === 'US') ? 'United States' :
                            ((agency.billing?.billingInfo || agency.billingInfo)?.country === 'CA') ? 'Canada' :
                              ((agency.billing?.billingInfo || agency.billingInfo)?.country === 'AU') ? 'Australia' :
                                ((agency.billing?.billingInfo || agency.billingInfo)?.country === 'DE') ? 'Germany' :
                                  ((agency.billing?.billingInfo || agency.billingInfo)?.country === 'FR') ? 'France' :
                                    ((agency.billing?.billingInfo || agency.billingInfo)?.country || '')}
                        disabled={true}
                        className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 cursor-not-allowed rounded-lg"
                        placeholder="Not provided"
                      />
                    )}
                    {isEditingBilling && billingValidationErrors.country && (
                      <p className="text-red-500 text-sm mt-1">{billingValidationErrors.country}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      VAT Number (Optional)
                    </label>
                    <input
                      type="text"
                      value={isEditingBilling ? editBillingData.vatNumber : ((agency.billing?.billingInfo || agency.billingInfo)?.vatNumber || '')}
                      onChange={isEditingBilling ? (e) => handleBillingInputChange('vatNumber', e.target.value) : undefined}
                      disabled={!isEditingBilling}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isEditingBilling
                        ? billingValidationErrors.vatNumber
                          ? 'border-red-300 dark:border-red-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                          : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                        : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 cursor-not-allowed'
                        }`}
                      placeholder={isEditingBilling ? "Enter VAT number" : (!(agency.billing?.billingInfo || agency.billingInfo)?.vatNumber ? "Not provided" : "")}
                    />
                    {isEditingBilling && billingValidationErrors.vatNumber && (
                      <p className="text-red-500 text-sm mt-1">{billingValidationErrors.vatNumber}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Invoices */}
              <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                    Invoices
                  </h3>
                </div>

                {getAgencySubscriptions(agency).length > 0 ? (
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Recent Invoices</h4>
                      {isLoadingInvoices ? (
                        <div className="space-y-2">
                          {[1, 2, 3].map((i) => (
                            <div key={i} className="animate-pulse p-3 bg-slate-100 dark:bg-slate-700 rounded-lg">
                              <div className="flex items-center space-x-3">
                                <div className="h-4 bg-slate-200 dark:bg-slate-600 rounded w-24"></div>
                                <div className="h-4 bg-slate-200 dark:bg-slate-600 rounded w-20"></div>
                                <div className="h-4 bg-slate-200 dark:bg-slate-600 rounded w-16"></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : invoices.length > 0 ? (
                        <div className="space-y-2">
                          {invoices.slice(0, 4).map((invoice) => (
                            <div key={invoice.id} className="flex items-center space-x-3 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                              {/* Circular download icon on left */}
                              <a
                                href={invoice.pdfUrl || invoice.downloadUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-shrink-0 inline-flex items-center justify-center w-10 h-10 bg-primary-600 hover:bg-primary-700 text-white rounded-full transition-colors"
                                title="Download PDF"
                              >
                                <BiDownload size={18} />
                              </a>

                              {/* Main content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between">
                                  <div>
                                    <div className="font-medium text-slate-900 dark:text-white">
                                      {invoice.number || invoice.id}
                                    </div>
                                    <div className="text-sm text-slate-500 dark:text-slate-400">
                                      {new Date(invoice.date).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric'
                                      })}
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className="font-medium text-slate-900 dark:text-white">
                                      {formatInvoiceAmount(invoice)}
                                    </div>
                                    <div className={`text-xs font-medium ${invoice.status === 'Paid'
                                      ? 'text-green-600 dark:text-green-400'
                                      : invoice.status === 'Pending'
                                        ? 'text-yellow-600 dark:text-yellow-400'
                                        : 'text-red-600 dark:text-red-400'
                                      }`}>
                                      {invoice.status}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                          <div className="text-sm text-slate-500 dark:text-slate-400 text-center pt-2">
                            Showing {Math.min(4, invoices.length)} of {invoices.length} invoices
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <p className="text-slate-500 dark:text-slate-400">No invoices found</p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-slate-500 dark:text-slate-400 mb-4">
                      No active subscription found
                    </p>
                    <button
                      onClick={() => navigate(`/agencies/create?step=2&agency=${agency.id}`)}
                      className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      Subscribe Now
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Subscription Management - Full Width */}
            {getAgencySubscriptions(agency).length > 0 && (
              <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                    Current Subscription
                  </h3>
                  {isLoadingSubscription ? (
                    <div className="animate-pulse">
                      <div className="h-6 bg-slate-200 dark:bg-slate-600 rounded w-24"></div>
                    </div>
                  ) : subscription ? (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${subscription.status === 'active'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                      : subscription.status === 'past_due'
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                      }`}>
                      {subscription.status === 'active' ? 'Active' :
                        subscription.status === 'past_due' ? 'Past Due' :
                          subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300">
                      Loading...
                    </span>
                  )}
                </div>

                {isLoadingSubscription ? (
                  <div className="space-y-4">
                    <div className="animate-pulse">
                      <div className="h-20 bg-slate-200 dark:bg-slate-600 rounded"></div>
                    </div>
                  </div>
                ) : subscription ? (
                  <div className="space-y-6">
                    {/* Current Plan */}
                    <div className="border-b border-slate-200 dark:border-slate-700 pb-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium text-slate-900 dark:text-white">
                            {getAgencyBasePlanCost(agency) === 4999 ? 'Pro Plan' : 'Agency Plan'}
                          </h4>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            Base subscription
                          </p>
                          <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                            {subscription.cancel_at_period_end ? (
                              `Ends: ${new Date(subscription.current_period_end).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}`
                            ) : subscription.status === 'active' ? (
                              `Next billing: ${new Date(subscription.current_period_end).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}`
                            ) : (
                              `Period: ${new Date(subscription.current_period_start).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric'
                              })} - ${new Date(subscription.current_period_end).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}`
                            )}
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-semibold text-slate-900 dark:text-white">
                            £{(getAgencyBasePlanCost(agency) / 100).toFixed(2)}
                          </div>
                          <div className="text-sm text-slate-500 dark:text-slate-400">per month</div>
                        </div>
                      </div>
                    </div>

                    {/* Seats Display (Read-Only) */}
                    <div>
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="font-medium text-slate-900 dark:text-white">Additional Seats</h4>
                          {(() => {
                            const agencySeatCost = getAgencySeatCost(agency)
                            const seatCostPounds = agencySeatCost / 100
                            const agencySeatCostForComparison = getAgencySeatCost(agency)
                            const seatItems = subscription.items.filter(item => item.price.unit_amount === agencySeatCostForComparison)

                            // Calculate billable agents (non-admin, licensed, agency-paid)
                            const billableAgents = agency?.agents?.filter(agent =>
                              agent.role !== 'admin' &&
                              agent.license.status === 'licensed' &&
                              agent.paymentResponsibility === 'agency'
                            ) || []
                            const billableSeats = billableAgents.length

                            const totalSeatCost = seatItems.reduce((total, item) => total + (item.price.unit_amount * item.quantity), 0) / 100
                            const expectedCost = billableSeats * seatCostPounds
                            const isProrated = Math.abs(totalSeatCost - expectedCost) > 0.01 // Allow for small rounding differences

                            return (
                              <p className="text-sm text-slate-500 dark:text-slate-400">
                                {isProrated ? (
                                  <>
                                    £{seatCostPounds.toFixed(2)} per seat per month
                                  </>
                                ) : (
                                  `£${seatCostPounds.toFixed(2)} per seat per month`
                                )}
                              </p>
                            )
                          })()}
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-semibold text-slate-900 dark:text-white">
                            {(() => {
                              // Calculate billable agents (non-admin, licensed, agency-paid)
                              const billableAgents = agency?.agents?.filter(agent =>
                                agent.role !== 'admin' &&
                                agent.license.status === 'licensed' &&
                                agent.paymentResponsibility === 'agency'
                              ) || []
                              return billableAgents.length
                            })()} seats
                          </div>
                          <div className="text-sm text-slate-500 dark:text-slate-400">
                            {(() => {
                              const agencySeatCost = getAgencySeatCost(agency)
                              const seatItems = subscription.items.filter(item => item.price.unit_amount === agencySeatCost)
                              const actualSeatCostFromStripe = seatItems.reduce((total, item) => total + (item.price.unit_amount * item.quantity), 0) / 100

                              // Calculate billable agents (non-admin, licensed, agency-paid)
                              const billableAgents = agency?.agents?.filter(agent =>
                                agent.role !== 'admin' &&
                                agent.license.status === 'licensed' &&
                                agent.paymentResponsibility === 'agency'
                              ) || []
                              const billableSeats = billableAgents.length
                              const calculatedSeatCost = billableSeats * (agencySeatCost / 100)

                              // If there are no seat items in Stripe, show calculated cost
                              if (seatItems.length === 0) {
                                return `£${calculatedSeatCost.toFixed(2)}/month`
                              }

                              const isProrated = Math.abs(actualSeatCostFromStripe - calculatedSeatCost) > 0.01

                              return isProrated ? (
                                <>
                                  £{actualSeatCostFromStripe.toFixed(2)}/month
                                </>
                              ) : (
                                `£${actualSeatCostFromStripe.toFixed(2)}/month`
                              )
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Total Cost Summary */}
                    <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-medium text-slate-900 dark:text-white">Monthly Total</span>
                        </div>
                        <div className="text-right">
                          <span className="text-xl font-semibold text-slate-900 dark:text-white">
                            £{(() => {
                              const basePlanCost = getAgencyBasePlanCost(agency) / 100
                              const agencySeatCost = getAgencySeatCost(agency)
                              const seatItems = subscription?.items.filter(item => item.price.unit_amount === agencySeatCost) || []

                              // Calculate billable agents (non-admin, licensed, agency-paid)
                              const billableAgents = agency?.agents?.filter(agent =>
                                agent.role !== 'admin' &&
                                agent.license.status === 'licensed' &&
                                agent.paymentResponsibility === 'agency'
                              ) || []
                              const billableSeats = billableAgents.length

                              // If there are seat items in Stripe, use those costs
                              if (seatItems.length > 0) {
                                const totalSeatCost = seatItems.reduce((total, item) => total + (item.price.unit_amount * item.quantity), 0) / 100
                                return (basePlanCost + totalSeatCost).toFixed(2)
                              } else {
                                // Calculate based on billable seats and agency pricing
                                const calculatedSeatCost = billableSeats * (agencySeatCost / 100)
                                return (basePlanCost + calculatedSeatCost).toFixed(2)
                              }
                            })()}
                          </span>
                        </div>
                      </div>
                      <div className="text-sm text-slate-500 dark:text-slate-400 text-right mt-1">
                        {subscription?.cancel_at_period_end ? (
                          `Ends: ${new Date(subscription?.current_period_end || Date.now()).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}`
                        ) : subscription?.status === 'active' ? (
                          `Next billing: ${new Date(subscription?.current_period_end || Date.now()).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}`
                        ) : (
                          `Period ends: ${new Date(subscription?.current_period_end || Date.now()).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}`
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-slate-500 dark:text-slate-400">
                      Failed to load subscription details
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Previous Subscriptions */}
            {getAgencySubscriptions(agency).length > 1 && (
              <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                    Previous Subscriptions
                  </h3>
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    {getAgencySubscriptions(agency).length - 1} older subscription{getAgencySubscriptions(agency).length > 2 ? 's' : ''}
                  </span>
                </div>

                <div className="space-y-2">
                  {getAgencySubscriptions(agency)
                    .sort((a, b) => new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime())
                    .slice(1) // Skip the first (latest) subscription
                    .map((sub, index) => (
                      <div
                        key={sub.stripeSubscriptionId || index}
                        className="border border-slate-200 dark:border-slate-600 rounded-lg"
                      >
                        <button
                          onClick={() => toggleSubscriptionExpansion(sub.stripeSubscriptionId || `sub-${index}`)}
                          className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`w-3 h-3 rounded-full ${sub.stripeSubscriptionStatus ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                            <div>
                              <p className="font-medium text-slate-900 dark:text-white">
                                Subscription #{sub.stripeSubscriptionId?.slice(-8) || 'Unknown'}
                              </p>
                              <p className="text-sm text-slate-500 dark:text-slate-400">
                                Created {sub.createdAt ? new Date(sub.createdAt).toLocaleDateString() : 'Unknown date'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 text-xs rounded-full ${sub.stripeSubscriptionStatus ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'}`}>
                              {sub.stripeSubscriptionStatus ? 'Active' : 'Inactive'}
                            </span>
                            {expandedSubscriptions.has(sub.stripeSubscriptionId || `sub-${index}`) ? (
                              <BiChevronUp className="text-slate-500 dark:text-slate-400" size={20} />
                            ) : (
                              <BiChevronDown className="text-slate-500 dark:text-slate-400" size={20} />
                            )}
                          </div>
                        </button>

                        {expandedSubscriptions.has(sub.stripeSubscriptionId || `sub-${index}`) && (
                          <div className="border-t border-slate-200 dark:border-slate-600 p-4 bg-slate-50 dark:bg-slate-700">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <p className="text-slate-600 dark:text-slate-400">Subscription ID</p>
                                <p className="font-mono text-slate-900 dark:text-white">{sub.stripeSubscriptionId || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-slate-600 dark:text-slate-400">Customer ID</p>
                                <p className="font-mono text-slate-900 dark:text-white">{sub.stripeCustomerId || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-slate-600 dark:text-slate-400">Price ID</p>
                                <p className="font-mono text-slate-900 dark:text-white">{sub.stripePriceId || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-slate-600 dark:text-slate-400">Status</p>
                                <p className="capitalize text-slate-900 dark:text-white">{sub.stripeSubscriptionStatus ? 'Active' : 'Inactive'}</p>
                              </div>
                              <div>
                                <p className="text-slate-600 dark:text-slate-400">Created</p>
                                <p className="text-slate-900 dark:text-white">
                                  {sub.createdAt ? new Date(sub.createdAt).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  }) : 'N/A'}
                                </p>
                              </div>
                              <div>
                                <p className="text-slate-600 dark:text-slate-400">Last Updated</p>
                                <p className="text-slate-900 dark:text-white">
                                  {sub.updatedAt ? new Date(sub.updatedAt).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  }) : 'N/A'}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            )}

          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Settings Header */}
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Agency Settings
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Configure agency-specific settings and preferences
                </p>
              </div>
            </div>

            {/* Alert */}
            {settingsMessage && settingsMessageType && (
              <Alert
                type={settingsMessageType}
                message={settingsMessage}
                onClose={() => {
                  setSettingsMessage('')
                  setSettingsMessageType('')
                }}
              />
            )}

            {/* Settings Content */}
            <div className="grid gap-6 md:grid-cols-1">
              {/* Billing Settings */}
              <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
                <Formik
                  initialValues={{
                    defaultAgencySeatCost: penceToDecimals(agency.settings?.defaultAgencySeatCost !== undefined ? agency.settings.defaultAgencySeatCost : 1499),
                    defaultAgencyBasePlanCost: penceToDecimals(agency.settings?.defaultAgencyBasePlanCost !== undefined ? agency.settings.defaultAgencyBasePlanCost : 4999)
                  }}
                  validationSchema={billingSettingsSchema}
                  enableReinitialize={true}
                  onSubmit={async (values, { setSubmitting, setStatus }) => {
                    if (!agency) return

                    try {
                      setStatus(null)
                      setSettingsMessage('')
                      setSettingsMessageType('')

                      const updatedAgency = await agencyService.updateAgency(agency.id, {
                        settings: {
                          defaultAgencySeatCost: decimalsToPence(formatCurrencyForSave(values.defaultAgencySeatCost)),
                          defaultAgencyBasePlanCost: decimalsToPence(formatCurrencyForSave(values.defaultAgencyBasePlanCost))
                        }
                      })

                      setAgency(updatedAgency)
                      setIsEditingSettings(false)
                      setSettingsMessage('Settings updated successfully!')
                      setSettingsMessageType('success')

                      setTimeout(() => {
                        setSettingsMessage('')
                        setSettingsMessageType('')
                      }, 3000)
                    } catch (err: any) {
                      console.error('Error updating agency settings:', err)
                      setSettingsMessage(err?.message || 'Failed to update agency settings. Please try again.')
                      setSettingsMessageType('error')
                    } finally {
                      setSubmitting(false)
                    }
                  }}
                >
                  {({ values, errors, touched, handleChange, handleSubmit, isSubmitting, setFieldValue, resetForm }) => (
                    <Form>
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="text-md font-medium text-slate-900 dark:text-white">
                          Billing Settings
                        </h4>
                        <div className="flex items-center space-x-2">
                          {isEditingSettings ? (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  setIsEditingSettings(false)
                                  setSettingsMessage('')
                                  setSettingsMessageType('')
                                  resetForm()
                                }}
                                disabled={isSubmitting}
                                className="inline-flex items-center space-x-2 px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
                              >
                                <BiX size={16} />
                                <span>Cancel</span>
                              </button>
                              <button
                                type="submit"
                                disabled={isSubmitting}
                                className="inline-flex items-center space-x-2 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-slate-400 disabled:cursor-not-allowed rounded-lg transition-colors disabled:opacity-50"
                              >
                                {isSubmitting ? (
                                  <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span>Saving...</span>
                                  </>
                                ) : (
                                  <>
                                    <BiCheck size={16} />
                                    <span>Save</span>
                                  </>
                                )}
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                setIsEditingSettings(true)
                                setSettingsMessage('')
                                setSettingsMessageType('')
                              }}
                              className="inline-flex items-center space-x-2 px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                            >
                              <BiEdit size={16} />
                              <span>Edit</span>
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                            Agency Seat Cost
                          </label>
                          <div className="relative">
                            <Field name="defaultAgencySeatCost">
                              {({ field, meta }) => (
                                <div className={`flex border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent ${isEditingSettings
                                  ? meta.touched && meta.error
                                    ? 'border-red-300 dark:border-red-600'
                                    : 'border-slate-300 dark:border-slate-600'
                                  : 'border-slate-200 dark:border-slate-700'
                                  }`}>
                                  <div className={`flex items-center px-3 ${isEditingSettings
                                    ? 'bg-white dark:bg-slate-700'
                                    : 'bg-slate-50 dark:bg-slate-800'
                                    } border-r border-slate-300 dark:border-slate-600`}>
                                    <span className="text-sm text-slate-600 dark:text-slate-400">£</span>
                                  </div>
                                  <input
                                    {...field}
                                    type="text"
                                    value={isEditingSettings ? values.defaultAgencySeatCost : penceToDecimals(agency.settings?.defaultAgencySeatCost !== undefined ? agency.settings.defaultAgencySeatCost : 1499)}
                                    onChange={(e) => {
                                      if (isEditingSettings) {
                                        const formattedValue = formatCurrencyInput(e.target.value)
                                        setFieldValue('defaultAgencySeatCost', formattedValue)
                                      }
                                    }}
                                    disabled={!isEditingSettings}
                                    placeholder="14.99"
                                    className={`flex-1 px-3 py-2 bg-transparent focus:outline-none text-sm ${isEditingSettings
                                      ? 'text-slate-900 dark:text-white'
                                      : 'text-slate-700 dark:text-slate-300 cursor-not-allowed'
                                      }`}
                                  />
                                  <div className={`flex items-center px-3 ${isEditingSettings
                                    ? 'bg-white dark:bg-slate-700'
                                    : 'bg-slate-50 dark:bg-slate-800'
                                    } border-l border-slate-300 dark:border-slate-600`}>
                                    <span className="text-sm text-slate-500 dark:text-slate-400">GBP</span>
                                  </div>
                                </div>
                              )}
                            </Field>
                          </div>
                          {isEditingSettings && errors.defaultAgencySeatCost && touched.defaultAgencySeatCost && (
                            <p className="text-red-500 text-sm mt-1">{errors.defaultAgencySeatCost}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                            Agency Base Plan Cost
                          </label>
                          <div className="relative">
                            <Field name="defaultAgencyBasePlanCost">
                              {({ field, meta }) => (
                                <div className={`flex border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent ${isEditingSettings
                                  ? meta.touched && meta.error
                                    ? 'border-red-300 dark:border-red-600'
                                    : 'border-slate-300 dark:border-slate-600'
                                  : 'border-slate-200 dark:border-slate-700'
                                  }`}>
                                  <div className={`flex items-center px-3 ${isEditingSettings
                                    ? 'bg-white dark:bg-slate-700'
                                    : 'bg-slate-50 dark:bg-slate-800'
                                    } border-r border-slate-300 dark:border-slate-600`}>
                                    <span className="text-sm text-slate-600 dark:text-slate-400">£</span>
                                  </div>
                                  <input
                                    {...field}
                                    type="text"
                                    value={isEditingSettings ? values.defaultAgencyBasePlanCost : penceToDecimals(agency.settings?.defaultAgencyBasePlanCost !== undefined ? agency.settings.defaultAgencyBasePlanCost : 4999)}
                                    onChange={(e) => {
                                      if (isEditingSettings) {
                                        const formattedValue = formatCurrencyInput(e.target.value)
                                        setFieldValue('defaultAgencyBasePlanCost', formattedValue)
                                      }
                                    }}
                                    disabled={!isEditingSettings}
                                    placeholder="49.99"
                                    className={`flex-1 px-3 py-2 bg-transparent focus:outline-none text-sm ${isEditingSettings
                                      ? 'text-slate-900 dark:text-white'
                                      : 'text-slate-700 dark:text-slate-300 cursor-not-allowed'
                                      }`}
                                  />
                                  <div className={`flex items-center px-3 ${isEditingSettings
                                    ? 'bg-white dark:bg-slate-700'
                                    : 'bg-slate-50 dark:bg-slate-800'
                                    } border-l border-slate-300 dark:border-slate-600`}>
                                    <span className="text-sm text-slate-500 dark:text-slate-400">GBP</span>
                                  </div>
                                </div>
                              )}
                            </Field>
                          </div>
                          {isEditingSettings && errors.defaultAgencyBasePlanCost && touched.defaultAgencyBasePlanCost && (
                            <p className="text-red-500 text-sm mt-1">{errors.defaultAgencyBasePlanCost}</p>
                          )}
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'audit' && (
          <div className="space-y-6">
            {/* Audit Header */}
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Audit Trail
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Complete history of all agent-related events in this agency
                </p>
              </div>
            </div>

            {/* Audit Events List */}
            {agency.auditInfo && agency.auditInfo.events && agency.auditInfo.events.length > 0 ? (
              <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <div className="divide-y divide-slate-200 dark:divide-slate-700">
                  {agency.auditInfo.events
                    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                    .map((event, index) => {
                      // Event type styling
                      const getEventStyle = () => {
                        switch (event.eventType) {
                          case 'agent_invited':
                          case 'agent_join_requested':
                            return {
                              bg: 'bg-blue-50 dark:bg-blue-900/10',
                              border: 'border-blue-200 dark:border-blue-800',
                              text: 'text-blue-700 dark:text-blue-300',
                              icon: '👋'
                            }
                          case 'invite_accepted':
                            return {
                              bg: 'bg-green-50 dark:bg-green-900/10',
                              border: 'border-green-200 dark:border-green-800',
                              text: 'text-green-700 dark:text-green-300',
                              icon: '✅'
                            }
                          case 'invite_rejected':
                          case 'invite_revoked':
                            return {
                              bg: 'bg-red-50 dark:bg-red-900/10',
                              border: 'border-red-200 dark:border-red-800',
                              text: 'text-red-700 dark:text-red-300',
                              icon: '❌'
                            }
                          case 'trial_started':
                            return {
                              bg: 'bg-yellow-50 dark:bg-yellow-900/10',
                              border: 'border-yellow-200 dark:border-yellow-800',
                              text: 'text-yellow-700 dark:text-yellow-300',
                              icon: '🎁'
                            }
                          case 'agent_licensed':
                            return {
                              bg: 'bg-purple-50 dark:bg-purple-900/10',
                              border: 'border-purple-200 dark:border-purple-800',
                              text: 'text-purple-700 dark:text-purple-300',
                              icon: '💳'
                            }
                          case 'agent_removed':
                            return {
                              bg: 'bg-orange-50 dark:bg-orange-900/10',
                              border: 'border-orange-200 dark:border-orange-800',
                              text: 'text-orange-700 dark:text-orange-300',
                              icon: '🚪'
                            }
                          case 'agent_synced':
                          case 'agency_synced':
                            return {
                              bg: 'bg-cyan-50 dark:bg-cyan-900/10',
                              border: 'border-cyan-200 dark:border-cyan-800',
                              text: 'text-cyan-700 dark:text-cyan-300',
                              icon: '🔄'
                            }
                          default:
                            return {
                              bg: 'bg-slate-50 dark:bg-slate-700/50',
                              border: 'border-slate-200 dark:border-slate-600',
                              text: 'text-slate-700 dark:text-slate-300',
                              icon: '📝'
                            }
                        }
                      }

                      const style = getEventStyle()

                      return (
                        <div key={index} className="p-4 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                          <div className="flex gap-4">
                            {/* Icon */}
                            <div className={`flex-shrink-0 w-10 h-10 rounded-full ${style.bg} border ${style.border} flex items-center justify-center text-lg`}>
                              {style.icon}
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900 dark:text-white">
                                    {event.description}
                                  </p>

                                  {/* Agent and Performer Info */}
                                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                                    {event.agentEmail && (
                                      <span>
                                        <strong>Agent:</strong> {event.agentEmail}
                                      </span>
                                    )}
                                    {event.performedByName && (
                                      <span>
                                        <strong>By:</strong> {event.performedByName}
                                      </span>
                                    )}
                                  </div>

                                  {/* Metadata */}
                                  {event.metadata && Object.keys(event.metadata).length > 0 && (
                                    <div className="mt-2 text-xs">
                                      <details className="group">
                                        <summary className="cursor-pointer text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 flex items-center gap-1">
                                          <span>View details</span>
                                          <BiChevronDown className="w-3 h-3 group-open:rotate-180 transition-transform" />
                                        </summary>
                                        <div className="mt-2 p-2 bg-slate-100 dark:bg-slate-800 rounded text-slate-600 dark:text-slate-400 font-mono text-xs overflow-auto">
                                          {Object.entries(event.metadata).map(([key, value]) => (
                                            <div key={key} className="flex gap-2">
                                              <span className="font-semibold">{key}:</span>
                                              <span>{JSON.stringify(value)}</span>
                                            </div>
                                          ))}
                                        </div>
                                      </details>
                                    </div>
                                  )}
                                </div>

                                {/* Timestamp */}
                                <div className="flex-shrink-0 text-right">
                                  <p className="text-xs text-slate-500 dark:text-slate-400">
                                    {new Date(event.timestamp).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric'
                                    })}
                                  </p>
                                  <p className="text-xs text-slate-400 dark:text-slate-500">
                                    {new Date(event.timestamp).toLocaleTimeString('en-US', {
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                </div>
              </div>
            ) : (
              <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-12 text-center">
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mb-4">
                    <BiListUl className="w-8 h-8 text-slate-400 dark:text-slate-500" />
                  </div>
                  <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                    No Audit Events
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm">
                    No audit events have been recorded for this agency yet. Events will appear here as actions are performed.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Invite Agent Modal */}
      <InviteAgentModal
        isOpen={showInviteModal}
        agencyId={agency?.id || ''}
        agencyName={agency?.name || ''}
        onClose={() => setShowInviteModal(false)}
        onInviteSent={() => {
          setShowInviteModal(false)
          if (activeTab === 'agents') {
            fetchAgents() // Refresh the agents table with pagination
          } else {
            fetchAgencyData() // Refresh all data if not on agents tab
          }
        }}
      />

      {/* Add Agent Modal */}
      <AddAgentModal
        isOpen={showAddAgentModal}
        agencyId={agency?.id || ''}
        agencyName={agency?.name || ''}
        onClose={() => setShowAddAgentModal(false)}
        onAgentAdded={() => {
          setShowAddAgentModal(false)
          if (activeTab === 'agents') {
            fetchAgents() // Refresh the agents table with pagination
          } else {
            fetchAgencyData() // Refresh all data if not on agents tab
          }
        }}
      />

      {/* Share Code Modal */}
      <ShareCodeModal
        isOpen={showShareCodeModal}
        agencyCode={agency?.agencyCode || ''}
        agencyName={agency?.name || ''}
        onClose={() => setShowShareCodeModal(false)}
      />

      {/* Edit Agent Modal */}
      <EditAgentModal
        isOpen={showEditModal}
        agent={selectedAgent}
        agencyId={agency?.id || ''}
        onClose={handleCloseEditModal}
        onAgentUpdated={handleAgentUpdated}
      />

      {/* Cancel Invite Confirmation Modal */}
      {showCancelInviteModal && agentToCancel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                  <BiX className="text-red-600 dark:text-red-400" size={20} />
                </div>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-slate-900 dark:text-white">
                  Cancel Invitation
                </h3>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Are you sure you want to cancel the invitation for{' '}
                <span className="font-medium text-slate-900 dark:text-white">
                  {agentToCancel.user?.firstName && agentToCancel.user?.lastName
                    ? `${agentToCancel.user.firstName} ${agentToCancel.user.lastName}`
                    : agentToCancel.user?.email || 'this user'
                  }
                </span>?
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-500 mt-2">
                This action cannot be undone. The user will no longer be able to accept this invitation.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCloseCancelInviteModal}
                disabled={isCancellingInvite}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmCancelInvite}
                disabled={isCancellingInvite}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-red-400 rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                {isCancellingInvite ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Cancelling...</span>
                  </>
                ) : (
                  <>
                    <BiX size={16} />
                    <span>Cancel Invitation</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Agent Confirmation Modal */}
      {showDeleteAgentModal && agentToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                  <BiX className="text-red-600 dark:text-red-400" size={20} />
                </div>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-slate-900 dark:text-white">
                  Delete Agent
                </h3>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Are you sure you want to delete{' '}
                <span className="font-medium text-slate-900 dark:text-white">
                  {agentToDelete.user?.firstName && agentToDelete.user?.lastName
                    ? `${agentToDelete.user.firstName} ${agentToDelete.user.lastName}`
                    : agentToDelete.user?.email || 'this user'
                  }
                </span>{' '}
                from the agency?
              </p>
              <p className="text-sm text-red-600 dark:text-red-400 mt-2 font-medium">
                This action cannot be undone. The agent will be permanently removed from the agency.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCloseDeleteAgentModal}
                disabled={isDeletingAgent}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeleteAgent}
                disabled={isDeletingAgent}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-red-400 rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                {isDeletingAgent ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <BiX size={16} />
                    <span>Delete Agent</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Join Request Modal */}
      {showJoinRequestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg max-w-md w-full mx-4 max-h-screen overflow-y-auto border border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Accept Join Request</h2>
                </div>
              </div>
              <button
                onClick={closeJoinRequestModal}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Role Field */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  <Shield className="w-4 h-4 inline mr-2" />
                  Role
                </label>
                <select
                  value={joinRequestRole}
                  onChange={e => setJoinRequestRole(e.target.value as 'agent' | 'admin')}
                  className="w-full p-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="agent">Agent</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {/* Payment Responsibility Field */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  <CreditCard className="w-4 h-4 inline mr-2" />
                  Payment Responsibility
                </label>
                <select
                  value={joinRequestPayment}
                  onChange={e => setJoinRequestPayment(e.target.value as 'agent' | 'agency')}
                  className="w-full p-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="agency">Agency Pays</option>
                  <option value="agent">Agent Pays</option>
                </select>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeJoinRequestModal}
                  className="flex-1 px-4 py-2 text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
                  disabled={isAcceptingJoinRequest}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmAcceptJoinRequest}
                  disabled={isAcceptingJoinRequest}
                  className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-slate-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {isAcceptingJoinRequest ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Accepting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Accept Request
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Agent Details Modal */}
      <AgentDetailsModal
        agent={selectedAgentForDetails}
        agency={agency}
        isOpen={showDetailsModal}
        onClose={handleCloseDetailsModal}
      />
    </div>
  )
}

export default AgencyProfile